import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { sendPlaylistEvent } from '../../utils/playlistEvents';

export default function VideoBlock({ block, playlist, blockIndex, isActive, onComplete, persistentCTA }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [watchStartTime, setWatchStartTime] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const longPressTimer = useRef(null);
  
  const progressTracked = useRef({ q25: false, q50: false, q75: false, complete: false });

  const videoUrl = block.props?.mp4_url || block.props?.video_url || '';
  const posterUrl = block.props?.poster || '';

  // NO AUTOPLAY - video stays paused until user interacts
  // When block becomes inactive, pause video
  useEffect(() => {
    if (!isActive && videoRef.current) {
      videoRef.current.pause();
      // setIsPlaying will be handled by onPause event
    }
  }, [isActive]);

  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch((err) => {
        // Silently ignore play() rejections on iOS
        console.log('[VideoBlock] play() rejected:', err.name);
      });
      setIsPlaying(true);
      if (videoRef.current.currentTime === 0) {
        setWatchStartTime(Date.now());
        sendPlaylistEvent(playlist.id, 'block_start', {
          block_id: block.id,
          block_index: blockIndex,
          block_type: 'video'
        });
      }
    }
  }, [isPlaying, playlist.id, block.id, blockIndex]);

  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;

    const percent = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(percent);

    if (percent >= 25 && !progressTracked.current.q25) {
      sendPlaylistEvent(playlist.id, 'block_quarter', {
        block_id: block.id,
        block_index: blockIndex,
        quarter: 1
      });
      progressTracked.current.q25 = true;
    }
    if (percent >= 50 && !progressTracked.current.q50) {
      sendPlaylistEvent(playlist.id, 'block_quarter', {
        block_id: block.id,
        block_index: blockIndex,
        quarter: 2
      });
      progressTracked.current.q50 = true;
    }
    if (percent >= 75 && !progressTracked.current.q75) {
      sendPlaylistEvent(playlist.id, 'block_quarter', {
        block_id: block.id,
        block_index: blockIndex,
        quarter: 3
      });
      progressTracked.current.q75 = true;
    }
    if (percent >= 95 && !progressTracked.current.complete) {
      sendPlaylistEvent(playlist.id, 'block_complete', {
        block_id: block.id,
        block_index: blockIndex,
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
      });
      progressTracked.current.complete = true;
    }
  }, [playlist.id, block.id, blockIndex, watchStartTime]);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  const handleCTAClick = useCallback((e) => {
    e.stopPropagation();
    sendPlaylistEvent(playlist.id, 'cta_click', {
      block_id: block.id,
      block_index: blockIndex,
      url: persistentCTA?.url,
      label: persistentCTA?.label
    });
    if (persistentCTA?.url) {
      window.open(persistentCTA.url, '_blank', 'noopener,noreferrer');
    }
  }, [playlist.id, block.id, blockIndex, persistentCTA]);

  // Long-press for pause/resume
  const handleTouchStart = useCallback((e) => {
    longPressTimer.current = setTimeout(() => {
      if (isPlaying) {
        videoRef.current?.pause();
        setIsPaused(true);
      }
    }, 500);
  }, [isPlaying]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    if (isPaused) {
      videoRef.current?.play().catch((err) => {
        // Silently ignore play() rejections on iOS
        console.log('[VideoBlock] touchEnd play() rejected:', err.name);
      });
      setIsPaused(false);
    } else {
      handlePlayPause();
    }
  }, [isPaused, handlePlayPause]);

  const handleEnded = useCallback(() => {
    if (onComplete) {
      sendPlaylistEvent(playlist.id, 'block_complete', {
        block_id: block.id,
        block_index: blockIndex,
        watch_ms: watchStartTime ? Date.now() - watchStartTime : 0
      });
      onComplete();
    }
  }, [onComplete, playlist.id, block.id, blockIndex, watchStartTime]);

  return (
    <div className="relative w-full h-full bg-black" style={{ aspectRatio: '9/16' }}>
      {/* Video */}
      <video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl}
        className="w-full h-full object-contain"
        playsInline
        muted={isMuted}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        crossOrigin="anonymous"
      />

      {/* Tap area */}
      <div
        className="absolute inset-0"
        onClick={handlePlayPause}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ zIndex: 1 }}
      />

      {/* Mute toggle */}
      <div 
        className="absolute flex gap-2 items-center" 
        style={{ 
          top: 'calc(env(safe-area-inset-top, 0px) + 4rem)',
          right: '1rem',
          zIndex: 10 
        }}
      >
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full bg-black/40 text-white hover:bg-black/60 backdrop-blur-sm"
          onClick={toggleMute}
        >
          {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </Button>
      </div>

      {/* Persistent CTA - bottom */}
      {persistentCTA && (
        <div 
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"
          style={{ 
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            paddingTop: '4rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            zIndex: 10 
          }}
        >
          <Button
            onClick={handleCTAClick}
            className="w-full sm:w-auto bg-white text-black hover:bg-gray-200 font-semibold"
            size="lg"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {persistentCTA.label}
          </Button>
        </div>
      )}

      {/* Pause indicator */}
      {isPaused && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <div className="text-white text-6xl">⏸️</div>
        </div>
      )}
    </div>
  );
}
